import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Slider, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { runMultiMatches } from '../../utils/api';
import centralDiscoveryQueue from '../../utils/centralDiscoveryQueue';
import { handleAgentSelection, isAgenticModeEnabled } from '../../utils/agenticHelper';

/**
 * Inline Slider Template
 * Based on Figma design: https://www.figma.com/design/Nhga9ODmaPGwSQJjO8lQZi/Calibrate-Mate?node-id=331-9349&m=dev
 * Example: Experience Histogram
 * 
 * Design specs from Figma:
 * - Chat bubble with 10px border radius (10px 10px 10px 0px)
 * - Card with 12px border radius, stroke rgba(192, 197, 205, 0.5)
 * - Card header with bottom border #EAECF0
 * - Sparkline chart with #167FF9 gradient
 * - Slider with #167FF9 color, 20px thumbs with white border
 * - Stats: 18px title, 13px subtitle
 */
const InlineSliderTemplate = ({
  title = "Let's take a look at the ideal work experience for this role. Based on your search criteria, here's how your current employee base is distributed by years of experience. Go ahead and select a range below to refine your candidate search. 👇",
  chartTitle,
  chartSubtitle,
  value = [2, 17],
  min = 0,
  max = 30,
  candidateCount = 12000,
  onValueChange,
  onConfirmed,
  confirmButtonText = "Confirm Work Experience",
  readOnly = false,
  showSparkline = true,
  showSkipButton = false,
  onSkipDiscovery,
  skipButtonText = "Skip Discovery",
  // Agentic flow props
  enableAgenticToggle = true,
  agenticContext = '',
  topic = 'experience',
  sessionId = 'default-session'
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [realCandidateCount, setRealCandidateCount] = useState(candidateCount);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  
  // Agentic flow state
  const [agenticModeEnabled, setAgenticModeEnabled] = useState(isAgenticModeEnabled());
  const [agentProcessing, setAgentProcessing] = useState(false);
  const [agentReasoning, setAgentReasoning] = useState('');
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);

  // Calculate real candidate count based on selected experience range
  useEffect(() => {
    const calculateRealCandidateCount = async () => {
      setIsLoadingCount(true);
      
      try {
        // Get all completed queue items (like DistributionBarChartTemplate does)
        const allQueueItems = centralDiscoveryQueue.getAllItems();
        const completedItems = allQueueItems.filter(item => item.disco_done === true);
        
        if (completedItems.length === 0) {
          console.log('📊 InlineSlider: No completed queue items found, using default count');
          setRealCandidateCount(candidateCount);
          setIsLoadingCount(false);
          return;
        }

        // Construct ICP requirements from completed queue items (same as DistributionBarChartTemplate)
        const icpRequirements = completedItems.map(item => ({
          section_type: item.section_type || 'Must Have',
          section_name: item.section_name,
          section_id: item.section_id,
          bool_str: item.bool_str || ''
        }));

        console.log('📊 InlineSlider: Calling runMultiMatches with base requirements:', icpRequirements.length);

        // Create experience requirement for current slider range (in to_match_requirements)
        const experienceMinMonths = localValue[0] * 12;
        const experienceMaxMonths = localValue[1] * 12;
        const experienceRequirement = {
          section_type: 'Must Have',
          section_name: 'Overall Years of Experience',
          section_id: 'experience_slider_range',
          bool_str: `(workexp.overall_experience:${experienceMinMonths}-${experienceMaxMonths})`
        };

        // Call runMultiMatches with experience requirement in to_match_requirements (like experience strategy does)
        const multiMatchResults = await runMultiMatches(
          icpRequirements, // base requirements
          [], // to_match (empty)
          [experienceRequirement], // to_match_requirements (experience filter)
          { run_on_self_org: false } // Use market data, not current company employees
        );

        console.log('📊 InlineSlider: runMultiMatches results:', multiMatchResults);

        if (multiMatchResults && multiMatchResults.target_requirements && multiMatchResults.count_by_queries) {
          // Find the macro_id from target_requirements (should be our experience requirement)
          const experienceTarget = multiMatchResults.target_requirements.find(req => 
            req.section_name === 'Overall Years of Experience'
          );
          
          if (experienceTarget && experienceTarget.macro_id) {
            const macroId = experienceTarget.macro_id;
            const candidateCount = multiMatchResults.count_by_queries[macroId];
            
            if (candidateCount !== undefined) {
              setRealCandidateCount(candidateCount);
              console.log('📊 InlineSlider: Updated candidate count from macro_id', macroId, 'to:', candidateCount);
            } else {
              console.warn('📊 InlineSlider: macro_id not found in count_by_queries:', macroId);
              setRealCandidateCount(candidateCount);
            }
          } else {
            console.warn('📊 InlineSlider: No experience target with macro_id found');
            setRealCandidateCount(candidateCount);
          }
        } else if (multiMatchResults && multiMatchResults.base_filter_count !== undefined) {
          // Fallback to base_filter_count
          setRealCandidateCount(multiMatchResults.base_filter_count);
          console.log('📊 InlineSlider: Updated candidate count from base_filter_count to:', multiMatchResults.base_filter_count);
        } else {
          console.warn('📊 InlineSlider: No valid response structure, using default');
          setRealCandidateCount(candidateCount);
        }
        
      } catch (error) {
        console.error('📊 InlineSlider: Error calculating candidate count:', error);
        setRealCandidateCount(candidateCount); // Fallback to default
      } finally {
        setIsLoadingCount(false);
      }
    };

    calculateRealCandidateCount();
  }, [localValue, candidateCount]); // Recalculate when slider value changes

  // Format candidate count for display
  const formatCandidateCount = (count) => {
    if (count < 1000) {
      return count.toString();
    } else if (count < 10000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 100000) {
      return Math.round(count / 1000) + 'K';
    } else if (count < 1000000) {
      return Math.round(count / 1000) + 'K';
    } else {
      return Math.round(count / 1000000) + 'M';
    }
  };

  // Generate sparkline data points for the chart
  const generateSparklineData = () => {
    const points = [];
    const numPoints = 50;
    
    for (let i = 0; i <= numPoints; i++) {
      const x = (i / numPoints) * 100; // 0 to 100%
      const yearValue = (i / numPoints) * max; // 0 to max
      
      // Create a realistic distribution curve with peaks around 5-15 years
      const center1 = max * 0.27; // ~8 years for 30 max
      const center2 = max * 0.5;  // ~15 years for 30 max
      const spread = max * 0.13;  // ~4 years for 30 max
      
      const height1 = Math.exp(-Math.pow(yearValue - center1, 2) / (2 * Math.pow(spread, 2)));
      const height2 = Math.exp(-Math.pow(yearValue - center2, 2) / (2 * Math.pow(spread * 1.5, 2))) * 0.6;
      
      const combinedHeight = Math.max(0.1, (height1 + height2) * 60); // Scale to 0-60%
      
      points.push({ x, y: 100 - combinedHeight, yearValue }); // Invert Y for SVG coordinates
    }
    
    return points;
  };

  const sparklineData = generateSparklineData();

  // Create SVG path for the sparkline
  const createSparklinePath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve using quadratic bezier curves
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  // Create filled area path
  const createFilledPath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} 100`; // Start from bottom
    
    // Go to first point
    path += ` L ${points[0].x} ${points[0].y}`;
    
    // Create smooth curve
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      const cpy = (prev.y + curr.y) / 2;
      path += ` Q ${cpx} ${cpy} ${curr.x} ${curr.y}`;
    }
    
    // Close the path to create filled area
    path += ` L ${points[points.length - 1].x} 100 Z`;
    
    return path;
  };

  // Filter points for selected range
  const selectedPoints = sparklineData.filter(point => 
    point.yearValue >= localValue[0] && point.yearValue <= localValue[1]
  );

  const handleSliderChange = (event, newValue) => {
    if (readOnly) return;
    setLocalValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  // Agentic flow handlers
  const handleAgenticToggle = (event) => {
    const enabled = event.target.checked;
    setAgenticModeEnabled(enabled);
    
    // Import the agentic helper functions
    import('../../utils/agenticHelper').then(({ enableAgenticMode, disableAgenticMode }) => {
      if (enabled) {
        enableAgenticMode(sessionId);
        console.log('🤖 AGENTIC MODE: Enabled globally for session:', sessionId);
        console.log('🤖 AGENTIC MODE: Template toggled, about to trigger selection');
        // Auto-trigger agent selection when toggled on
        if (!agentProcessing) {
          handleAgentAutoSelect();
        }
      } else {
        disableAgenticMode();
        console.log('👤 MANUAL MODE: Disabled agentic flow globally');
      }
    });
  };

  const handleAgentAutoSelect = useCallback(async () => {
    if (readOnly || agentProcessing) return;
    
    setAgentProcessing(true);
    setAgentReasoning('');
    
    try {
      const strategyContext = {
        topic,
        context: agenticContext || `Experience range selection for ${topic}. Available range: ${min} to ${max} years. Current selection: ${localValue[0]} to ${localValue[1]} years. Please analyze and select an optimal experience range that balances candidate pool size with role relevance.`,
        selectionType: 'experience-range',
        systemMessage: `You are a recruiter helping to calibrate candidate search criteria for experience range selection. 

CRITICAL: You must return a JSON response in this EXACT format:
{
  "selected_range": [min_years, max_years],
  "reasoning": "Brief explanation of why this range was selected"
}

Guidelines:
- min_years and max_years must be integers between ${min} and ${max}
- min_years must be less than max_years
- Consider common experience ranges: Entry (0-2), Junior (2-5), Mid (3-8), Senior (5-15), Expert (10+)
- Balance between having enough candidates and role requirements
- Typical ranges: 2-5 years, 3-8 years, 5-12 years, etc.

Current context: ${agenticContext || 'Experience range selection'}`,
        range_min: min,
        range_max: max,
        current_range: localValue
      };

      const agentSelection = await handleAgentSelection(strategyContext);
      
      console.log('🤖 AGENT SELECTION: Raw response:', agentSelection);
      
      // Try to parse the range from different possible response formats
      let selectedRange = null;
      let reasoning = '';
      
      if (agentSelection) {
        // Format 1: Direct selected_range property
        if (agentSelection.selected_range && Array.isArray(agentSelection.selected_range)) {
          selectedRange = agentSelection.selected_range;
          reasoning = agentSelection.reasoning || '';
        }
        // Format 2: Try parsing from selected_options if it contains range info
        else if (agentSelection.selected_options && Array.isArray(agentSelection.selected_options)) {
          const rangeOption = agentSelection.selected_options[0];
          if (typeof rangeOption === 'string' && rangeOption.includes('-')) {
            const match = rangeOption.match(/(\d+)-(\d+)/);
            if (match) {
              selectedRange = [parseInt(match[1]), parseInt(match[2])];
            }
          }
          reasoning = agentSelection.reasoning || '';
        }
        // Format 3: Try parsing raw response as JSON if it's a string
        else if (typeof agentSelection === 'string') {
          try {
            const parsed = JSON.parse(agentSelection);
            if (parsed.selected_range && Array.isArray(parsed.selected_range)) {
              selectedRange = parsed.selected_range;
              reasoning = parsed.reasoning || '';
            }
          } catch (e) {
            console.warn('Could not parse agent response as JSON:', e);
          }
        }
      }
      
      // Validate and apply the range
      if (selectedRange && Array.isArray(selectedRange) && selectedRange.length === 2) {
        const [selectedMin, selectedMax] = selectedRange.map(val => parseInt(val));
        
        if (typeof selectedMin === 'number' && typeof selectedMax === 'number' && 
            !isNaN(selectedMin) && !isNaN(selectedMax) &&
            selectedMin >= min && selectedMax <= max && selectedMin < selectedMax) {
          
          console.log('🤖 AGENT SELECTION: Applying range selection:', [selectedMin, selectedMax]);
          setLocalValue([selectedMin, selectedMax]);
          setAgentReasoning(reasoning || `AI selected ${selectedMin}-${selectedMax} year experience range`);
          
          // Call onValueChange if provided
          if (onValueChange) {
            onValueChange([selectedMin, selectedMax]);
          }
          
          console.log('✅ AGENT SELECTION: Successfully applied range selection');
        } else {
          console.error('🚨 AGENT SELECTION: Invalid range values:', selectedRange);
          setAgentReasoning('Invalid range received from AI. Please select manually.');
        }
      } else {
        console.error('🚨 AGENT SELECTION: No valid range in response:', agentSelection);
        setAgentReasoning('AI could not determine optimal range. Please select manually.');
      }
      
    } catch (error) {
      console.error('🚨 AGENT AUTO-SELECT: Error:', error);
      setAgentReasoning('Error occurred during automatic selection. Please select manually.');
    } finally {
      setAgentProcessing(false);
    }
  }, [readOnly, agentProcessing, topic, agenticContext, title, min, max, localValue, onValueChange]);

  // Auto-trigger agent selection if agentic mode is already enabled
  useEffect(() => {
    if (isAgenticModeEnabled() && !agentProcessing && !readOnly && !hasAutoTriggered) {
      console.log('🤖 AUTO-TRIGGER: Agentic mode already enabled, auto-triggering selection');
      setHasAutoTriggered(true);
      setAgenticModeEnabled(true);
      setTimeout(() => {
        handleAgentAutoSelect();
      }, 500); // Small delay to ensure UI is ready
    }
  }, [hasAutoTriggered]); // Only depend on hasAutoTriggered to prevent multiple triggers

  const handleConfirm = () => {
    setIsConfirmed(true);
    if (onConfirmed) {
      onConfirmed(localValue);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        padding: '0px 8px',
        backgroundColor: 'transparent',
      }}
    >
      {/* Chat Bubble Container */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px 10px 10px 0px',
          padding: '0px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* Title Text */}
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '1.375em',
            color: '#101828',
            textAlign: 'left',
          }}
        >
          {title}
        </Typography>

        {/* Inline Slider Card */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            height: '312px', // Fixed height from Figma
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              borderBottom: '1px solid #EAECF0',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '1.375em',
                    color: '#101828',
                  }}
                >
                  {chartTitle}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chart Content */}
          <Box
            sx={{
              padding: '20px 20px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
            }}
          >
            {/* Stats */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                padding: '12px',
                marginBottom: '20px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '1.3333333333333333em',
                  color: '#101828',
                  textAlign: 'left',
                }}
              >
                {localValue[0]} - {localValue[1]} years
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isLoadingCount ? (
                  <>
                    <CircularProgress size={12} sx={{ color: '#667085' }} />
                    <Typography
                      sx={{
                        fontFamily: 'Roboto',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: '1.3846153846153846em',
                        color: '#667085',
                        textAlign: 'left',
                      }}
                    >
                      Calculating...
                    </Typography>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '13px',
                      fontWeight: 400,
                      lineHeight: '1.3846153846153846em',
                      color: '#667085',
                      textAlign: 'left',
                    }}
                  >
                    {formatCandidateCount(realCandidateCount)} candidates
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Sparkline Chart */}
            {showSparkline && (
              <Box
                sx={{
                  width: '100%',
                  height: '100px',
                  position: 'relative',
                  marginBottom: '20px',
                }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                >
                  {/* Background (disabled) area */}
                  <path
                    d={createFilledPath(sparklineData)}
                    fill="url(#backgroundGradientTemplate)"
                    opacity="0.2"
                  />
                  <path
                    d={createSparklinePath(sparklineData)}
                    stroke="#167FF9"
                    strokeWidth="2.5"
                    fill="none"
                    opacity="0.2"
                  />
                  
                  {/* Active (selected) area */}
                  {selectedPoints.length > 1 && (
                    <>
                      <path
                        d={createFilledPath(selectedPoints)}
                        fill="url(#activeGradientTemplate)"
                      />
                      <path
                        d={createSparklinePath(selectedPoints)}
                        stroke="#167FF9"
                        strokeWidth="2.5"
                        fill="none"
                      />
                    </>
                  )}
                  
                  {/* Gradient definitions */}
                  <defs>
                    <linearGradient id="backgroundGradientTemplate" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                      <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="activeGradientTemplate" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#167FF9" stopOpacity="1" />
                      <stop offset="81.25%" stopColor="#167FF9" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </Box>
            )}

            {/* Range Slider */}
            <Box sx={{ width: '100%', position: 'relative', paddingBottom: '10px' }}>
              <Slider
                value={localValue}
                onChange={handleSliderChange}
                min={min}
                max={max}
                disabled={readOnly}
                sx={{
                  color: '#167FF9', // Exact Figma color
                  height: '4px',
                  padding: '15px 0',
                  '& .MuiSlider-track': { 
                    bgcolor: '#167FF9', 
                    height: '4px',
                    border: 'none',
                    borderRadius: '20px'
                  },
                  '& .MuiSlider-thumb': {
                    bgcolor: '#167FF9',
                    border: '2px solid #FFFFFF', // Exact Figma spec
                    width: '20px', // Exact Figma size
                    height: '20px', // Exact Figma size
                    borderRadius: '20px', // Exact Figma spec
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '&:hover': {
                      boxShadow: '0 0 0 8px rgba(22, 127, 249, 0.16)'
                    },
                    '&:focus, &:hover, &.Mui-active': {
                      boxShadow: '0 0 0 8px rgba(22, 127, 249, 0.16)'
                    },
                    '&:before': {
                      display: 'none'
                    }
                  },
                  '& .MuiSlider-rail': { 
                    bgcolor: '#EAECF0', // Exact Figma color
                    height: '4px',
                    borderRadius: '20px'
                  },
                  '& .MuiSlider-valueLabel': {
                    display: 'none'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Agentic Toggle Row */}
        {enableAgenticToggle && !readOnly && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingX: '8px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={agenticModeEnabled}
                  onChange={handleAgenticToggle}
                  disabled={agentProcessing}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4599FA',
                      '&:hover': {
                        backgroundColor: 'rgba(69, 153, 250, 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4599FA',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: agenticModeEnabled ? '#4599FA' : '#9CA3AF' }} />
                  <Typography variant="body2" sx={{ fontSize: 14, color: agenticModeEnabled ? '#4599FA' : '#9CA3AF' }}>
                    {agentProcessing ? 'AI Assistant is selecting...' : 'Let AI Assistant select for me'}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
            {agentReasoning && (
              <Typography variant="caption" sx={{ fontSize: 12, color: '#667085', marginLeft: 4 }}>
                🤖 {agentReasoning}
              </Typography>
            )}
          </Box>
        )}

        {/* Buttons Row - Confirm and Skip side by side */}
        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isConfirmed}
              sx={{
                backgroundColor: '#4599FA',
                color: '#FFFFFF',
                fontFamily: 'Roboto',
                fontSize: '14px',
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                height: '36px',
                '&:hover': {
                  backgroundColor: '#3b82d6',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(69, 153, 250, 0.3)',
                  color: '#FFFFFF',
                }
              }}
            >
              {isConfirmed ? `${confirmButtonText} ✓` : confirmButtonText}
            </Button>
            
            {/* Skip Button */}
            {showSkipButton && (
              <Button
                variant="outlined"
                onClick={onSkipDiscovery}
                sx={{
                  color: '#667085',
                  borderColor: '#D0D5DD',
                  backgroundColor: '#F9FAFB',
                  fontWeight: 400,
                  fontSize: '14px',
                  padding: '8px 16px',
                  height: '36px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#F2F4F7',
                    borderColor: '#B8BCC8',
                  }
                }}
              >
                {skipButtonText}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InlineSliderTemplate;
